import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from '../analytics';
import DarkModeToggle from "./DarkModeToggle";
import ShareButton from "./ShareButton";

const navItems = [
  { to: "overview", label: "Overview" },
  { to: "product-timeline", label: "Product Timeline" },
  { to: "product-principles", label: "Product Principles" },
  { to: "tech-stack", label: "Tech Stack" },
  { to: "prd", label: "PRD" },
  { to: "contact", label: "Contact" },
];

// Custom smooth scroll function
const smoothScrollTo = (elementId, offset = -80) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset + offset;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

// Custom ScrollLink component to replace react-scroll
// Uses named functions for event listeners to avoid React 19 compatibility issues
function ScrollLink({ to, offset, onClick, className, children }) {
  const handleClick = useCallback((e) => {
    e.preventDefault();
    if (onClick) onClick();
    smoothScrollTo(to, offset);
  }, [to, offset, onClick]);

  return (
    <a
      href={`#${to}`}
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  );
}

function Sidebar({ isOpen = false, onClose }) {
  const [activeSection, setActiveSection] = useState("overview");
  const isMobile = onClose != null;

  const handleLinkClick = (sectionId) => {
    trackEvent('nav_clicked', {
      section: sectionId,
    });
    if (isMobile) onClose();
  };

  // Update active section on scroll when sidebar is visible
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => ({
        id: item.to,
        element: document.getElementById(item.to)
      })).filter(s => s.element);

      const scrollPosition = window.pageYOffset + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element) {
          const elementTop = section.element.getBoundingClientRect().top + window.pageYOffset;
          if (scrollPosition >= elementTop - 80) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check on mount

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* Backdrop overlay - mobile only */}
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/40 dark:bg-black/60 md:hidden"
            onClick={onClose}
            aria-hidden
          />
        )}
      </AnimatePresence>

      {/* Sidebar panel */}
      <motion.aside
        initial={false}
        animate={{
          x: isMobile && !isOpen ? "-100%" : 0,
        }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={`fixed left-0 top-0 z-50 h-screen bg-white dark:bg-gray-900 shadow-[0_0_15px_rgba(0,0,0,0.06)] dark:shadow-[0_0_15px_rgba(0,0,0,0.3)] transition-shadow duration-200
          w-[280px] md:w-[240px] lg:w-[280px]
          ${isMobile ? "" : "md:translate-x-0"}`}
        aria-label="Main navigation"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 px-4 py-4 md:px-5 md:py-5 lg:px-6 lg:py-6">
            <span
              className="text-lg font-semibold tracking-tight md:text-xl text-primary-500 dark:text-primary-400"
            >
              Elizabeth Blege
            </span>
            <div className="flex items-center gap-2">
              <DarkModeToggle />
              {isMobile && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  aria-label="Close menu"
                >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                </button>
              )}
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4 md:px-2.5 lg:px-3">
            <ul className="space-y-0.5">
              {navItems.map((item) => (
                <li key={item.to}>
                  <ScrollLink
                    to={item.to}
                    offset={-80}
                    onClick={() => handleLinkClick(item.to)}
                    className={`block rounded-lg border-l-4 py-2.5 pl-3 pr-3 text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${
                      activeSection === item.to
                        ? "border-primary-300 dark:border-primary-400 bg-primary-500 dark:bg-primary-600 text-white"
                        : "border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    {item.label}
                  </ScrollLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Share button at bottom */}
          <div className="border-t border-gray-100 dark:border-gray-800 p-4">
            <ShareButton openUpward={true} />
          </div>
        </div>
      </motion.aside>
    </>
  );
}

export default Sidebar;
